/* eslint-disable no-duplicate-imports */
import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { clone } from '@jack-henry/frontend-utils/functions';
import { DynamicComponentExitReason } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { TmDetailedError } from '@treasury/domain/shared';
import { StopPaymentsService } from '@treasury/domain/stop-payments';
import { UsersService } from '@treasury/domain/users';
import { TmContainer } from '@treasury/presentation';
import '@treasury/presentation/components/tm-list-header';
import { TmSearchTermChangeEvent } from '@treasury/presentation/components/tm-list-header';
import '@treasury/presentation/components/tm-loader';
import { getStatusConfig } from '@treasury/presentation/components/tm-status';
import '@treasury/presentation/components/vendor/tm-pagination-controls';
import { TmPaginationControls } from '@treasury/presentation/components/vendor/tm-pagination-controls';
import { TmDialogButtonPosture, TmDialogService } from '@treasury/presentation/services/dialog';
import { ContextMenuItem } from '@vaadin/context-menu';
import '@vaadin/grid';
import { GridSelectedItemsChangedEvent } from '@vaadin/grid';
import { columnBodyRenderer, GridColumnBodyLitRenderer } from '@vaadin/grid/lit.js';
import '@vaadin/grid/vaadin-grid-column.js';
import { format } from 'date-fns/format';
import { css, html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { filterTableResults } from '../../../utilities/table-result-filter';
import '../../components/templates/treasury-status-column.template';
import { StopPaymentVm } from '../data/stop-payment.vm';

export const tagName = 'stop-payments-table';
@customElement(tagName)
export class StopPaymentsTable extends TmContainer {
    @property({ type: Array })
    public stopPaymentVms = [];

    @InjectProperty()
    declare private usersService: UsersService;

    @InjectProperty()
    declare private readonly dialogService: TmDialogService;

    @InjectProperty()
    declare private stopPaymentsService: StopPaymentsService;

    @state()
    private filteredStopPaymentVms: StopPaymentVm[] = [];

    @state()
    private selectedStopPayments: StopPaymentVm[] = [];

    @state()
    private filteredStopPaymentsCount = 0;

    @state()
    private searchTerm = '';

    @query('tm-pagination-controls')
    private paginationControls!: TmPaginationControls;

    private get fieldsToFilter() {
        return this.isDesktop
            ? [
                  'account.accountDisplayLabel',
                  'checkNumbers',
                  'checkAmountFormatted',
                  'checkDateFormatted',
                  'createdDateFormatted',
                  'expirationDateFormatted',
                  'status',
                  'payee',
              ]
            : [
                  'status',
                  'checkNumbers',
                  'account.accountDisplayLabel',
                  'checkAmountFormatted',
                  'checkDateFormatted',
              ];
    }

    public updated(changedProps: Map<string, unknown>) {
        if (changedProps.has('stopPaymentVms')) {
            this.filteredStopPaymentVms = this.stopPaymentVms;
            this.filteredStopPaymentsCount = this.stopPaymentVms.length;
            this.selectedStopPayments = [];
        }
    }

    protected render() {
        return html`<tm-section
            class=${classMap({
                'flex flex-col': true,
                'overflow-hidden h-full bg-page': !this.isDesktop,
                'h-full mx-2 py-2': this.isDesktop,
            })}
        >
            <div class="flex-none">${this.renderTableHeader()}</div>
            <div class="flex-grow">${this.renderTable()}</div>
            <div class="flex-none">${this.renderPaginationControls()}</div>
        </tm-section>`;
    }

    private stopPaymentClicked(stopPayment: StopPaymentVm) {
        if (!stopPayment) {
            return;
        }
        this.dispatchEvent(new CustomEvent('stop-payment-clicked', { detail: stopPayment }));
    }

    private filterResults(e?: CustomEvent) {
        let filteredStopPayments = clone(this.stopPaymentVms);
        if (e) {
            this.searchTerm = e.detail.value;
        }

        filteredStopPayments = filterTableResults(
            {
                detail: { value: this.searchTerm },
            } as CustomEvent,
            this.stopPaymentVms,
            this.fieldsToFilter
        ) as [];

        this.filteredStopPaymentsCount = filteredStopPayments.length;
        if (this.isDesktop) {
            const offset = this.paginationControls?.calculateOffset();
            const limit = this.paginationControls?.pageSize;
            this.filteredStopPaymentVms = filteredStopPayments.slice(offset, offset + limit);
        } else {
            this.filteredStopPaymentVms = filteredStopPayments;
        }
    }

    private renderTableHeader() {
        return html`<tm-list-header
            class=${classMap({
                'bg-primary': !this.isDesktop,
            })}
            .placeholder=${'Search stop payments'}
            .filterButton=${true}
            .searchTerm=${this.searchTerm}
            @filterButtonClick=${() => this.dispatchEvent(new CustomEvent('filter-stop-payments'))}
            @search-term-change=${(e: TmSearchTermChangeEvent) => this.filterResults(e)}
        ></tm-list-header>`;
    }

    private renderTable() {
        if (this.loading) {
            return html`<tm-loader card></tm-loader>`;
        }
        if (!this.stopPaymentVms.length) {
            return html`<div
                class=${classMap({
                    'w-full px-4 py-8 bg-primary text-center text-sm text-secondary': true,
                    'shadow-md': !this.isDesktop,
                })}
            >
                No stop payments found
            </div>`;
        }
        const isSelectable = (item: StopPaymentVm) => {
            return (
                item.status === 'Pending Approval' &&
                item.permissions?.some(p => p.permission === 'Approve')
            );
        };
        return html`<vaadin-grid
            class=${classMap({
                'h-full': true,
                'shadow-md': !this.isDesktop,
            })}
            .items=${this.filteredStopPaymentVms}
            .isItemSelectable=${isSelectable}
            @active-item-changed=${({ detail }: CustomEvent) => {
                this.stopPaymentClicked(detail.value);
            }}
            @selected-items-changed="${(e: GridSelectedItemsChangedEvent<StopPaymentVm>) => {
                this.selectedStopPayments = e.detail.value;
                this.dispatchEvent(
                    new CustomEvent('selection-changed', {
                        detail: this.selectedStopPayments,
                    })
                );
            }}"
        >
            ${this.renderColumns()}
        </vaadin-grid>`;
    }

    private renderColumns() {
        if (!this.isDesktop) {
            return html`<vaadin-grid-column
                    ${columnBodyRenderer(
                        this.renderPaymentDetailColumn as GridColumnBodyLitRenderer<StopPaymentVm>,
                        []
                    )}
                ></vaadin-grid-column>
                <vaadin-grid-column
                    text-align="end"
                    ${columnBodyRenderer(
                        this.renderAmountAndDateColumn as GridColumnBodyLitRenderer<StopPaymentVm>,
                        []
                    )}
                ></vaadin-grid-column>`;
        }
        return html`<vaadin-grid-selection-column></vaadin-grid-selection-column
            ><vaadin-grid-sort-column
                header="Account"
                path="account.accountDisplayLabel"
                ${columnBodyRenderer(
                    (item: StopPaymentVm) =>
                        html`<div class="flex flex-col">
                            <span class="text-sm">${item.account.accountDisplayLabel}</span>
                        </div>`,
                    []
                )}
            ></vaadin-grid-sort-column
            ><vaadin-grid-sort-column
                header="Check Number"
                path="checkNumbers"
                ${columnBodyRenderer(
                    (item: StopPaymentVm) =>
                        html`<div class="flex flex-col">
                            <span class="text-sm">${item.checkNumbers}</span>
                        </div>`,
                    []
                )}
            ></vaadin-grid-sort-column
            ><vaadin-grid-sort-column
                header="Amount"
                path="checkAmountFormatted"
                text-align="end"
                ${columnBodyRenderer(
                    (item: StopPaymentVm) =>
                        html`<div class="flex flex-col">
                            <span class="text-sm">${item.checkAmountFormatted}</span>
                        </div>`,
                    []
                )}
            ></vaadin-grid-sort-column
            ><vaadin-grid-sort-column
                header="Check Date"
                path="checkDateFormatted"
                ${columnBodyRenderer(
                    (item: StopPaymentVm) =>
                        html`<div class="flex flex-col">
                            <span class="text-sm">${item.checkDateFormatted}</span>
                        </div>`,
                    []
                )}
            ></vaadin-grid-sort-column
            ><vaadin-grid-sort-column
                header="Created Date"
                path="createdDateFormatted"
                ${columnBodyRenderer(
                    (item: StopPaymentVm) =>
                        html`<div class="flex flex-col">
                            <span class="text-sm">${item.createdDateFormatted}</span>
                        </div>`,
                    []
                )}
            ></vaadin-grid-sort-column
            ><vaadin-grid-sort-column
                header="Expiration Date"
                path="expirationDateFormatted"
                ${columnBodyRenderer(
                    (item: StopPaymentVm) =>
                        html`<div class="flex flex-col">
                            <span class="text-sm">${item.expirationDateFormatted}</span>
                        </div>`,
                    []
                )}
            ></vaadin-grid-sort-column
            ><vaadin-grid-sort-column
                header="Status"
                path="status"
                width="150px"
                ${columnBodyRenderer((payment: StopPaymentVm) => {
                    return html`<treasury-status-column
                        .item=${payment}
                        .completedApproversFunction=${async () =>
                            this.usersService.fetchCompletedApprovers({
                                approvalEntity: 'stopPayment',
                                productId: payment.id as number | undefined,
                                createdBy: payment.createdBy as unknown as number,
                            }) as unknown as string[]}
                        .eligibleApproversFunction=${async () =>
                            this.usersService.fetchAvailableApprovers({
                                approvalEntity: 'stopPayment',
                                productId: payment.id as number | undefined,
                                createdBy: payment.createdBy as unknown as number,
                            }) as unknown as string[]}
                    ></treasury-status-column>`;
                })}
            ></vaadin-grid-sort-column
            ><vaadin-grid-sort-column
                header="Payee"
                path="payee"
                ${columnBodyRenderer(
                    (item: StopPaymentVm) =>
                        html`<div class="flex flex-col">
                            <span class="text-sm">${item.payee}</span>
                        </div>`,
                    []
                )}
            ></vaadin-grid-sort-column
            ><vaadin-grid-column
                frozen-to-end
                flex-grow="0"
                auto-width
                @click=${(e: Event) => e.stopPropagation()}
                ${columnBodyRenderer((payment: StopPaymentVm) => {
                    const actions: Array<ContextMenuItem> = payment.permissions.some(
                        p => p.permission === 'Cancel'
                    )
                        ? [{ text: 'Cancel' }]
                        : [];
                    return html`<tm-actions-column
                        .items=${actions}
                        @action-selected=${() => {
                            this.cancelStopPayment(payment);
                        }}
                    ></tm-actions-column>`;
                })}
            ></vaadin-grid-column>`;
    }

    private renderPaymentDetailColumn(payment: StopPaymentVm) {
        return html`<div class="flex items-center">
            <div class="mr-3">
                <tm-status .status=${payment.status} .label=${false}></tm-status>
            </div>
            <div class="flex flex-col">
                <div class="flex items-center">
                    <div class="text-sm font-medium">
                        ${payment.checkNumbers.length > 1 ? 'Checks' : 'Check'}
                    </div>
                    <div class="ml-2 text-xs text-secondary">
                        ${payment.checkNumbers[0]}
                        ${payment.checkNumbers.length > 1
                            ? html` - ${payment.checkNumbers.at(-1)}`
                            : nothing}
                    </div>
                </div>
                <div class="text-xs text-secondary">${payment.account.accountDisplayLabel}</div>
                <div
                    class="text-xs text-secondary"
                    style=${`color: var(${getStatusConfig(payment.status).color})`}
                >
                    ${payment.status}
                </div>
            </div>
        </div>`;
    }

    private renderAmountAndDateColumn(payment: StopPaymentVm) {
        return html`<div class="flex flex-col justify-start items-end">
            <div class="text-sm font-medium">
                ${payment.checkAmount
                    ? payment.checkAmount.toLocaleString('en-US', {
                          style: 'currency',
                          currency: 'USD',
                      })
                    : nothing}
            </div>
            <div class="text-xs text-secondary">
                ${payment.checkDate ? format(new Date(payment.checkDate), 'P') : nothing}
            </div>
        </div>`;
    }

    private renderPaginationControls() {
        if (!this.isDesktop) {
            return nothing;
        }
        return html`<tm-pagination-controls
            .totalItemCount=${this.filteredStopPaymentsCount}
            @page-changed=${() => {
                this.filterResults();
            }}
        ></tm-pagination-controls>`;
    }

    private async cancelStopPayment(payment: StopPaymentVm) {
        const { reason } = await this.dialogService.open(
            html`<div>Are you sure you want to cancel this stop payment?</div>
                <br /><tm-text-field
                    label="Comments"
                    @value-changed=${(e: CustomEvent) => {
                        const { value } = e.detail;
                        payment.remarks = value;
                    }}
                ></tm-text-field>`,
            'Cancel Stop Payment',
            {
                buttons: {
                    [TmDialogButtonPosture.Primary]: {
                        label: 'Confirm',
                        reason: DynamicComponentExitReason.Confirm,
                    },
                    [TmDialogButtonPosture.Secondary]: {
                        label: 'Cancel',
                        reason: DynamicComponentExitReason.Cancel,
                    },
                },
            }
        );

        if (reason === DynamicComponentExitReason.Cancel) {
            return;
        }
        if (reason === DynamicComponentExitReason.Confirm) {
            this.loading = true;
            try {
                this.stopPaymentsService.cancelStopPayment(
                    payment.id as number,
                    payment.remarks.join(' ')
                );
                this.dispatchEvent(new CustomEvent('stop-payment-canceled', { detail: payment }));
                this.notificationService.renderSuccess('Stop payment canceled successfully');
            } catch (error) {
                if (error instanceof TmDetailedError) {
                    this.notificationService.renderError(error);
                } else if (error instanceof Error) {
                    this.notificationService.renderError(error);
                } else {
                    this.notificationService.renderError('Error processing your wire');
                }
            } finally {
                this.loading = false;
            }
        }
    }

    public static get styles() {
        return [
            css`
                vaadin-grid::part(cell) {
                    align-items: center;
                }
                vaadin-grid::part(header-cell) {
                    min-height: auto;
                    padding-top: 3px;
                    background-color: var(--border-color);
                    font-size: 0.75rem;
                    line-height: calc(1 / 0.75);
                }
                vaadin-grid-sorter::part(indicators) {
                    opacity: 0.5;
                }
                vaadin-grid-sorter[direction]::part(indicators) {
                    opacity: 100;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: StopPaymentsTable;
    }
}
